import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Star } from 'lucide-react';
import { products } from '../mockData';
import SEO from '../components/SEO';

export default function Products() {
  return (
    <div className="min-h-screen bg-white" data-testid="products-overview-page">
      <SEO
        title="Products | CriLam Decorative — 6H Acrylic Boards, CNC Doors, Wall Panels"
        description="Explore CriLam's full product range: Pre-Laminated 6H Acrylic Boards with CriLam PUR-Bond™ Technology, CNC Designer Doors, Decorative Wall Panels, Mica & Nail-Free Veneer Doors. Made in Kanpur, UP."
        canonical="https://crilamdecorative.com/products"
        keywords="CriLam products, 6H acrylic boards, CNC designer doors, decorative wall panels, mica doors, nail-free veneer doors"
        jsonLd={[{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'CriLam Decorative Product Range',
          itemListElement: products.map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: p.name,
            url: `https://crilamdecorative.com/products/${p.id}`,
          })),
        }]}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#004534]/30 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="label-mono text-[#D4AF37] mb-4">OUR PRODUCT RANGE</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit'] max-w-3xl">
              Every Product. One Promise. <span className="text-[#D4AF37]">CriLam PUR-Bond™.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              From flagship Pre-Laminated 6H Acrylic Boards to CNC Designer Doors — every CriLam product
              is factory-laminated with CriLam PUR-Bond™ Technology. Nail-free. Waterproof. 10-year written guarantee.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12 space-y-10">
          {products.map((product, idx) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center p-6 lg:p-10 rounded-2xl border ${
                product.flagship ? 'border-[#D4AF37]/50 bg-[#004534]/[0.03]' : 'border-gray-100 bg-white'
              }`}
              data-testid={`product-card-${product.id}`}
            >
              <div className={`relative overflow-hidden rounded-xl ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img src={product.hero} alt={product.name} className="w-full h-72 lg:h-96 object-cover" loading="lazy" />
                {product.flagship && (
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider rounded-full">
                    <Star className="h-3.5 w-3.5" /> Flagship
                  </span>
                )}
              </div>
              <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                <h2 className="text-2xl md:text-3xl font-bold text-[#004534] mb-2 font-['Outfit']">{product.name}</h2>
                <p className="text-sm text-[#D4AF37] font-semibold tracking-wide mb-4">{product.tagline}</p>
                <p className="text-[#525956] mb-6">{product.description}</p>
                <ul className="space-y-2 mb-6">
                  {product.features.slice(0, 3).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-[#004534] mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 mb-8">
                  {product.applications.slice(0, 4).map((a, i) => (
                    <span key={i} className="px-3 py-1 bg-[#004534]/5 text-[#004534] text-xs font-medium rounded-full">{a}</span>
                  ))}
                </div>
                <Link to={`/products/${product.id}`} data-testid={`product-link-${product.id}`}>
                  <motion.button
                    className="px-6 py-3 bg-[#004534] text-white font-medium hover:bg-[#D4AF37] hover:text-[#0A0D0C] transition-colors flex items-center gap-2 rounded-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Explore {product.name.split(' ')[0]} <ArrowRight className="h-4 w-4" />
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#004534]">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-lg md:text-lg font-bold text-white mb-4 font-['Outfit']">Not sure which product fits your project?</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">Use our AI Product Recommender or talk to our team — we'll guide you to the right CriLam PUR-Bond™ solution.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/product-recommender" data-testid="products-cta-recommender">
              <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors">Try AI Recommender</button>
            </Link>
            <Link to="/contact" data-testid="products-cta-contact">
              <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">Contact Us</button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
